import React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import ReactPocCard from './components/ReactPocCard';
import RoomLayoutMap from './components/RoomLayoutMap';
import type { RoomLayoutMapProps } from './components/RoomLayoutMap';
import StudioSpotsCard from './components/StudioSpotsCard';

/**
 * Adapter for the Angular `react-directive` bridge, which renders every registered
 * component with a single `data` prop. This spreads that `data` into RoomLayoutMap's
 * real props. Renders nothing when `data` is missing (and warns in dev builds — the
 * most common cause is a mis-typed `react-directive` scope expression producing a
 * silent blank render). Callback-reference stability across Angular digests is handled
 * inside RoomLayoutMap via ref-capture, so consumers do not need to memoise callbacks.
 */
function WebRoomLayoutMap({ data }: { data?: RoomLayoutMapProps }) {
  if (!data) {
    // Vite replaces `import.meta.env.DEV` at build time (false in the prod UMD bundle, so this
    // branch is stripped). Typed via `src/vite-env.d.ts` (vite/client).
    if (import.meta.env.DEV) {
      console.warn(
        '[WebRoomLayoutMap] Rendered without `data` — RoomLayoutMap will not mount. ' +
          'Check the Angular `react-directive` scope expression bound to this component.'
      );
    }
    return null;
  }
  return <RoomLayoutMap {...data} />;
}

export type WebReactRegistry = {
  ReactPocCard?: typeof ReactPocCard;
  StudioSpotsCard?: typeof StudioSpotsCard;
  RoomLayoutMap?: typeof WebRoomLayoutMap;
  __React?: typeof React;
  __ReactDOM?: typeof ReactDOMClient;
};

export type WebReactWindow = Omit<Window, 'WebReactComponents'> & {
  WebReactComponents?: WebReactRegistry;
};

function registerGlobals() {
  if (typeof window === 'undefined') {
    return;
  }

  const webWindow = window as WebReactWindow;
  const registry: WebReactRegistry = webWindow.WebReactComponents ?? {};
  webWindow.WebReactComponents = registry;
  registry.ReactPocCard = ReactPocCard;
  registry.StudioSpotsCard = StudioSpotsCard;
  registry.RoomLayoutMap = WebRoomLayoutMap;
  registry.__React = React;
  registry.__ReactDOM = ReactDOMClient;
}

registerGlobals();

export { ReactPocCard, StudioSpotsCard, WebRoomLayoutMap };
